/**
 * BOLETÍN del alumno (E2) — selectores PUROS del libro de notas visto por su
 * propio dueño.
 *
 * El gradebook del PROFESOR es una matriz `alumnos × ítems` que computa el
 * servidor (`use_cases::gradebook::compute_cohort_gradebook`, endpoint
 * `/api/cohorts/:id/gradebook`) y que el alumno **no puede** pedir: incluiría
 * las notas de sus compañeros. Pero las tres piezas de SU fila ya viajan a su
 * dispositivo por `my_cohorts_student` — `grade_category`, `grade_item`
 * (transparencia de pesos) y `assignment_grade` filtrado por `user_id` —, así
 * que su boletín se compone en el cliente sin pedir de más.
 *
 * `weightedFinal` es el ESPEJO PURO de `weighted_final` en Rust (mismo contrato:
 * fracción ausente ⇒ 0, `null` explícito ⇒ ítem omitido, `dropLowest` descarta
 * las peores sin quedarse sin ninguna, categoría vacía no contribuye, ítems sin
 * categoría forman un bucket de peso 1). Mantener EN SYNC con el Rust: la
 * paridad se verifica con los mismos casos en
 * `__tests__/report-card.test.ts` — es el mismo patrón que ya siguen
 * `grading/registry.ts` y `completion/registry.ts`.
 *
 * Lo que aquí es NUEVO —y no existe en el servidor— es la **nota proyectada**:
 * la que sale de ponderar SOLO lo ya calificado. La final del servidor cuenta
 * como 0 lo que aún no tiene nota (es una final, no una previsión), así que a
 * mitad de curso muestra un número desalentador y sin significado. La
 * proyección responde a la pregunta real del alumno: «con lo que llevo hecho,
 * ¿cómo voy?». Se obtiene con la MISMA función, omitiendo los ítems pendientes
 * igual que se omite un excusado — no hay una segunda ponderación.
 */

/** Fuentes de nota de un `grade_item` (== `source_kind` en el servidor). */
export type ReportCardSourceKind = "assignment" | "auto_blocks" | "attendance" | "manual";

/** Fila `grade_category` sincronizada (snake_case, campos tolerantes a null). */
export interface ReportCardCategoryRow {
  id: string;
  name?: string | null;
  weight?: number | null;
  drop_lowest?: number | null;
  position?: number | null;
}

/** Fila `grade_item` sincronizada (snake_case, campos tolerantes a null). */
export interface ReportCardItemRow {
  id: string;
  name?: string | null;
  category_id?: string | null;
  source_kind?: string | null;
  source_ref?: string | null;
  max_score?: number | null;
  weight?: number | null;
  position?: number | null;
}

/** MI nota de una tarea (`assignment_grade`, filas propias por sync). */
export interface ReportCardGrade {
  score: number | null;
  status: string | null;
}

/**
 * Las tres fuentes de las que sale la fracción de cada ítem, ya resueltas por
 * el llamante desde datos sincronizados:
 *  · `assignment`   → mi `assignment_grade` indexada por `assignment_id`.
 *  · `auto_blocks`  → media de `lesson_progress.score` del curso (0..1).
 *  · `attendance`   → `cohort_enrollment.attendance_rate` (0..1).
 *  · `manual`       → crédito fijo (v1 del servidor), sin fuente.
 * `null` en las dos últimas significa «todavía no hay dato», y entonces el
 * ítem cuenta como PENDIENTE para la proyección (no como cero).
 */
export interface ReportCardSources {
  gradeByAssignment: ReadonlyMap<string, ReportCardGrade>;
  autoBlocksFraction: number | null;
  attendanceFraction: number | null;
}

/** Una celda del boletín (un ítem con MI nota resuelta). */
export interface ReportCardCell {
  itemId: string;
  name: string;
  categoryId: string | null;
  sourceKind: ReportCardSourceKind;
  /** Máximo nativo del ítem (para mostrar «17 / 20»). */
  maxScore: number;
  weight: number;
  /** Nota nativa (0..maxScore); `null` si no hay o está excusada. */
  score: number | null;
  /** Fracción 0..1 que entra en la ponderación oficial (0 si no hay nota). */
  fraction: number;
  excused: boolean;
  /** ¿Tiene ya una nota firme? (lo que decide si entra en la proyección.) */
  graded: boolean;
}

/** Una categoría del boletín con sus celdas (pesos = transparencia). */
export interface ReportCardCategoryGroup {
  id: string | null;
  name: string;
  weight: number;
  dropLowest: number;
  cells: ReportCardCell[];
}

/** El boletín completo del alumno en una cohorte. */
export interface ReportCard {
  categories: ReportCardCategoryGroup[];
  cells: ReportCardCell[];
  /** Nota final CRUDA oficial (fracción 0..1): lo pendiente cuenta como 0. */
  officialRaw: number;
  /** Nota PROYECTADA cruda (fracción 0..1): solo lo ya calificado. */
  projectedRaw: number;
  /** Ítems con nota firme / ítems que pueden llegar a tenerla (sin excusados). */
  gradedCount: number;
  gradableCount: number;
}

/** Forma mínima de una categoría para la ponderación. */
export interface WeightedCategory {
  id: string;
  weight: number;
  dropLowest: number;
}

/** Forma mínima de un ítem para la ponderación. */
export interface WeightedItem {
  id: string;
  categoryId: string | null;
  weight: number;
}

/** Bucket virtual de los ítems sin categoría (peso 1, sin descartes). */
const UNCATEGORIZED = " uncategorized";

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));

/**
 * Nota final CRUDA (fracción 0..1) — espejo puro de `weighted_final` (Rust).
 *
 * `fractions` mapea `itemId → fracción`:
 *   · clave ausente        ⇒ 0 (no hay nota, cuenta en contra);
 *   · valor `null`         ⇒ el ítem se OMITE (excusado, o pendiente cuando se
 *     pide la proyección);
 *   · valor numérico       ⇒ se recorta a 0..1.
 */
export function weightedFinal(
  categories: readonly WeightedCategory[],
  items: readonly WeightedItem[],
  fractions: ReadonlyMap<string, number | null>,
): number {
  const catWeight = new Map<string, number>();
  const catDrop = new Map<string, number>();
  for (const c of categories) {
    catWeight.set(c.id, Math.max(0, c.weight));
    catDrop.set(c.id, Math.max(0, Math.trunc(c.dropLowest)));
  }

  // (peso del ítem, fracción) agrupados por categoría efectiva.
  const buckets = new Map<string, { weight: number; fraction: number }[]>();
  for (const item of items) {
    const raw = fractions.has(item.id) ? fractions.get(item.id) : 0;
    if (raw === null || raw === undefined) {
      continue;
    }
    const key =
      item.categoryId !== null && catWeight.has(item.categoryId) ? item.categoryId : UNCATEGORIZED;
    const bucket = buckets.get(key) ?? [];
    bucket.push({ weight: Math.max(0, item.weight), fraction: clamp01(raw) });
    buckets.set(key, bucket);
  }

  let num = 0;
  let den = 0;
  for (const [key, all] of buckets) {
    if (all.length === 0) {
      continue;
    }
    let entries = all;
    const drop = catDrop.get(key) ?? 0;
    if (drop > 0 && entries.length > 1) {
      // Conserva los `keep` de MAYOR fracción (nunca se queda sin ninguno).
      const keep = Math.max(1, entries.length - drop);
      entries = entries.toSorted((a, b) => a.fraction - b.fraction).slice(entries.length - keep);
    }
    const itemDen = entries.reduce((acc, e) => acc + e.weight, 0);
    const catScore =
      itemDen > 0
        ? entries.reduce((acc, e) => acc + e.weight * e.fraction, 0) / itemDen
        : // Todos los ítems con peso 0 → media simple.
          entries.reduce((acc, e) => acc + e.fraction, 0) / entries.length;
    const cw = key === UNCATEGORIZED ? 1 : (catWeight.get(key) ?? 1);
    num += cw * catScore;
    den += cw;
  }
  return den > 0 ? clamp01(num / den) : 0;
}

/** Coacciona una cadena a un `source_kind` conocido (defecto `manual`). */
function parseSourceKind(kind: string | null | undefined): ReportCardSourceKind {
  switch (kind) {
    case "assignment":
    case "auto_blocks":
    case "attendance": {
      return kind;
    }
    default: {
      return "manual";
    }
  }
}

/**
 * Resuelve MI celda de un ítem — espejo del mapeo `source_kind → fracción` que
 * hace `compute_cohort_gradebook` para cada alumno.
 */
function cellFor(item: ReportCardItemRow, sources: ReportCardSources): ReportCardCell {
  const sourceKind = parseSourceKind(item.source_kind);
  const maxScore = item.max_score ?? 0;
  const scale = maxScore > 0 ? maxScore : 1;

  let score: number | null = null;
  let fraction = 0;
  let excused = false;
  let graded = false;

  switch (sourceKind) {
    case "assignment": {
      const grade = sources.gradeByAssignment.get(item.source_ref ?? "");
      if (grade?.status === "excused") {
        excused = true;
      } else if (
        grade &&
        grade.score !== null &&
        (grade.status === "graded" || grade.status === "returned")
      ) {
        ({ score } = grade);
        fraction = clamp01(grade.score / scale);
        graded = true;
      }
      break;
    }
    case "auto_blocks": {
      if (sources.autoBlocksFraction !== null) {
        fraction = clamp01(sources.autoBlocksFraction);
        score = fraction * maxScore;
        graded = true;
      }
      break;
    }
    case "attendance": {
      if (sources.attendanceFraction !== null) {
        fraction = clamp01(sources.attendanceFraction);
        score = fraction * maxScore;
        graded = true;
      }
      break;
    }
    default: {
      // `manual` (v1 del servidor): crédito fijo del ítem.
      score = maxScore;
      fraction = 1;
      graded = true;
    }
  }

  return {
    itemId: item.id,
    name: item.name ?? "",
    categoryId: item.category_id ?? null,
    sourceKind,
    maxScore,
    weight: item.weight ?? 1,
    score,
    fraction,
    excused,
    graded,
  };
}

const byPosition = <T extends { position?: number | null; name?: string | null }>(
  a: T,
  b: T,
): number => {
  const diff = (a.position ?? 0) - (b.position ?? 0);
  return diff === 0 ? (a.name ?? "").localeCompare(b.name ?? "") : diff;
};

/**
 * Compone el boletín del alumno a partir de lo ya sincronizado.
 *
 * La nota oficial y la proyectada salen de la MISMA `weightedFinal`, cambiando
 * solo qué se hace con lo pendiente: contarlo como 0 (oficial, igual que el
 * servidor) u omitirlo (proyección).
 */
export function buildReportCard(
  categoryRows: readonly ReportCardCategoryRow[],
  itemRows: readonly ReportCardItemRow[],
  sources: ReportCardSources,
): ReportCard {
  const cells = itemRows.toSorted(byPosition).map((item) => cellFor(item, sources));

  const weightedCategories: WeightedCategory[] = categoryRows.map((c) => ({
    id: c.id,
    weight: c.weight ?? 1,
    dropLowest: c.drop_lowest ?? 0,
  }));
  const weightedItems: WeightedItem[] = cells.map((cell) => ({
    id: cell.itemId,
    categoryId: cell.categoryId,
    weight: cell.weight,
  }));

  const official = new Map<string, number | null>();
  const projected = new Map<string, number | null>();
  for (const cell of cells) {
    official.set(cell.itemId, cell.excused ? null : cell.fraction);
    // Pendiente ⇒ fuera de la proyección (igual que un excusado).
    projected.set(cell.itemId, cell.excused || !cell.graded ? null : cell.fraction);
  }

  const groups = new Map<string | null, ReportCardCategoryGroup>();
  for (const c of categoryRows.toSorted(byPosition)) {
    groups.set(c.id, {
      id: c.id,
      name: c.name ?? "",
      weight: c.weight ?? 1,
      dropLowest: c.drop_lowest ?? 0,
      cells: [],
    });
  }
  for (const cell of cells) {
    const key = cell.categoryId !== null && groups.has(cell.categoryId) ? cell.categoryId : null;
    let group = groups.get(key);
    if (!group) {
      group = { id: null, name: "", weight: 1, dropLowest: 0, cells: [] };
      groups.set(null, group);
    }
    group.cells.push(cell);
  }

  const gradable = cells.filter((cell) => !cell.excused);
  return {
    categories: [...groups.values()].filter((g) => g.cells.length > 0),
    cells,
    officialRaw: weightedFinal(weightedCategories, weightedItems, official),
    projectedRaw: weightedFinal(weightedCategories, weightedItems, projected),
    gradedCount: gradable.filter((cell) => cell.graded).length,
    gradableCount: gradable.length,
  };
}
